import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimatedSection } from '@/components/AnimatedSection';
import { useSEO } from '@/hooks/useSEO';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  ArrowRight, Phone, Mail, CheckCircle, Shield, Search, FileText,
  DollarSign, Clock, Scale, Globe
} from 'lucide-react';

const packages = [
  {
    name: 'Basic IRS Relief',
    price: '$750',
    description: 'For taxpayers who need help addressing relatively straightforward IRS problems.',
    features: [
      'IRS notice review',
      'IRS account and balance verification',
      'Standard installment agreement assistance',
      'Preparation and submission of applicable IRS forms',
      'IRS communication within the agreed scope',
    ],
    cta: 'Schedule My Free Consultation',
    highlight: false,
  },
  {
    name: 'IRS Resolution Plus',
    price: '$1,500',
    description: 'For taxpayers facing more complicated IRS collection matters.',
    features: [
      'IRS transcript and account review',
      'Financial analysis',
      'Nonstandard installment agreement negotiation',
      'Currently Not Collectible status requests',
      'Penalty relief evaluation',
      'IRS collection correspondence',
      'Assistance with applicable financial disclosure forms',
    ],
    cta: 'Discuss My IRS Options',
    highlight: true,
  },
  {
    name: 'Advanced Tax Resolution',
    price: '$2,995',
    description: 'For taxpayers facing substantial tax debt or complex collection issues.',
    features: [
      'Offer in Compromise evaluation, preparation, and submission',
      'Detailed financial analysis',
      'IRS collection negotiations',
      'Multiple-year tax liability analysis',
      'IRS levy or garnishment resolution assistance',
      'Preparation of financial disclosure statements',
      'Case follow-up within the agreed scope',
    ],
    cta: 'Request My Confidential Consultation',
    highlight: false,
  },
];

const additionalServices = [
  { service: 'IRS Case Investigation', price: '$295' },
  { service: 'IRS Notice Review & Response', price: '$250' },
  { service: 'IRS Payment Plan Assistance', price: '$750' },
  { service: 'Penalty Abatement', price: '$850' },
  { service: 'Currently Not Collectible Request', price: '$1,250' },
  { service: 'Wage Garnishment or Bank Levy Assistance', price: '$1,500' },
  { service: 'Tax Lien Relief Assistance', price: '$1,500' },
  { service: 'Offer in Compromise', price: '$2,995' },
  { service: 'IRS Audit Representation', price: '$1,500' },
  { service: 'Innocent Spouse Relief', price: '$2,000' },
  { service: 'Business Payroll Tax Resolution', price: '$2,500' },
  { service: 'Trust Fund Recovery Penalty Representation', price: '$3,500' },
];

const steps = [
  {
    number: '1',
    title: 'Free Initial Consultation',
    description: 'Tell us about your IRS problem. We\'ll discuss your concerns, review any immediate deadlines, and explain how we can assist.',
    note: null,
  },
  {
    number: '2',
    title: 'IRS Case Investigation',
    description: 'With proper authorization, we obtain and analyze relevant IRS account information, identify outstanding filing or collection issues, and evaluate potential resolution strategies.',
    note: 'Case investigation starts at $295.',
  },
  {
    number: '3',
    title: 'Your Personalized Resolution Plan',
    description: 'We explain your available options, recommended strategy, estimated professional fees, and the services included before you authorize further work.',
    note: null,
  },
  {
    number: '4',
    title: 'Resolution and Follow-Up',
    description: 'Our appropriately authorized tax professional works on the agreed services, communicates with the IRS when permitted, and provides updates on the progress of your case.',
    note: null,
  },
];

const whyChoose = [
  { icon: DollarSign, title: 'Transparent Pricing', description: 'Know the scope and proposed fees before engagement.' },
  { icon: Shield, title: 'Personalized Tax Assistance', description: 'Solutions based on your circumstances rather than one-size-fits-all promises.' },
  { icon: FileText, title: 'Individual & Business Tax Problems', description: 'Assistance with personal, self-employment, and business tax matters.' },
  { icon: Scale, title: 'Secure Document Handling', description: 'Share sensitive tax information through our secure client portal.' },
  { icon: Clock, title: 'Flexible Payment Arrangements', description: 'Available on qualifying engagements, subject to written terms.' },
];

const faqs = [
  {
    question: 'Can I settle my IRS tax debt for less than I owe?',
    answer: 'Possibly. The IRS Offer in Compromise program may allow eligible taxpayers to settle for less than the full balance. Acceptance depends on IRS criteria, including financial circumstances and ability to pay.',
  },
  {
    question: 'Can TaxPro-Answers stop wage garnishment or an IRS levy?',
    answer: 'We can evaluate available options and assist with appropriate IRS relief requests. Collection relief is subject to IRS procedures and case-specific circumstances.',
  },
  {
    question: 'Do you offer payment plans for professional fees?',
    answer: 'Flexible arrangements may be available on qualifying engagements. Terms are provided in writing before services begin.',
  },
  {
    question: 'Do I have to pay the full IRS debt before receiving assistance?',
    answer: 'No. Professional assistance may be available while tax liabilities remain outstanding. The IRS determines which payment or relief options are available.',
  },
  {
    question: 'Does TaxPro-Answers guarantee that the IRS will reduce my tax debt?',
    answer: 'No. IRS decisions depend on applicable law, eligibility, financial information, and the facts of each case.',
  },
];

export default function TaxReliefPricing() {
  useSEO({
    title: 'IRS Tax Relief & Resolution Pricing',
    description: 'Affordable IRS tax relief and resolution services with transparent pricing. Payment plans, penalty relief, offers in compromise, and more. Free consultation available.',
  });

  return (
    <div data-testid="tax-relief-pricing-page">
      {/* Hero */}
      <section className="bg-brand-navy py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-5">
              IRS Tax Relief &amp; Resolution Services
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none text-white mb-6">
              Owe the IRS? Get Professional Help at a Price You Can Understand.
            </h1>
            <p className="text-base text-slate-400 mb-4">
              Affordable Tax Resolution | Transparent Pricing | Personalized Service
            </p>
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              Are you receiving IRS collection notices, struggling with back taxes, facing a tax lien, or worried about wage garnishment?
            </p>
            <p className="text-base text-slate-400 leading-relaxed mb-4">
              TaxPro-Answers helps individuals, self-employed taxpayers, and business owners understand their options and pursue solutions to IRS tax problems.
            </p>
            <p className="text-base text-slate-400 leading-relaxed mb-10">
              Whether you owe $5,000 or $100,000 or more, we can review your circumstances and help determine which IRS relief programs may be available to you.
            </p>
            <p className="text-xl font-bold text-white mb-10">
              We Offer Affordable Payment Options for Those Who Qualify
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:+18555892461">
                <Button
                  data-testid="hero-call-btn"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4 mr-2" /> Call (855) 589-2461 — FREE Consultation
                </Button>
              </a>
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="hero-book-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  Schedule a Confidential Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Our Tax Resolution Pricing
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-4 max-w-2xl">
              We believe taxpayers deserve to understand the cost of professional assistance before making a financial commitment.
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden mt-14">
            {packages.map((pkg, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className={`p-8 h-full flex flex-col ${pkg.highlight ? 'bg-brand-muted' : 'bg-white'}`}>
                  {pkg.highlight && (
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-green mb-3">Most Popular</span>
                  )}
                  <h3 className="font-heading font-bold text-xl text-brand-navy mb-1">{pkg.name}</h3>
                  <p className="font-heading font-bold text-3xl text-brand-navy mb-1">
                    {pkg.price}
                  </p>
                  <p className="text-xs text-slate-400 mb-4">Starting at</p>
                  <p className="text-sm text-slate-500 leading-relaxed mb-6">{pkg.description}</p>
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Services may include:</p>
                  <div className="space-y-2 mb-8 flex-1">
                    {pkg.features.map((f, j) => (
                      <div key={j} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-brand-green mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{f}</span>
                      </div>
                    ))}
                  </div>
                  <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                    <Button
                      data-testid={`pricing-pkg-${i}`}
                      className={`w-full font-semibold py-5 rounded-md ${
                        pkg.highlight
                          ? 'bg-brand-green hover:bg-brand-green-hover text-white'
                          : 'bg-brand-navy hover:bg-brand-navy-light text-white'
                      }`}
                    >
                      {pkg.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.3}>
            <p className="text-xs text-slate-400 mt-6 max-w-2xl">
              Each package covers only the services identified in your written engagement agreement. Additional proceedings, tax returns, appeals, and unrelated tax matters may require separate fees.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Additional Services Table */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Additional Tax Resolution Services
            </p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy mb-10">
              Individual service pricing
            </h2>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 gap-0 px-6 py-3 bg-brand-navy">
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-300">Service</span>
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-300 text-right">Starting Price</span>
              </div>
              {additionalServices.map((item, i) => (
                <div key={i} className={`grid grid-cols-2 gap-0 px-6 py-4 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'} border-t border-slate-100`}>
                  <span className="text-sm text-slate-700">{item.service}</span>
                  <span className="text-sm font-semibold text-brand-navy text-right">{item.price}</span>
                </div>
              ))}
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.2} className="mt-10 text-center">
            <p className="text-base text-slate-500 mb-6">
              Not sure which service you need? You don't have to figure it out alone.
            </p>
            <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
              <Button
                data-testid="additional-cta"
                className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-8 py-6 text-base rounded-md"
              >
                Schedule a Free Tax Resolution Consultation
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              How Our Tax Resolution Process Works
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {steps.map((step, i) => (
              <AnimatedSection key={i} delay={i * 0.1}>
                <div className="border border-slate-200 rounded-lg p-8 h-full">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-brand-navy flex items-center justify-center flex-shrink-0">
                      <span className="text-brand-green font-heading font-bold text-lg">{step.number}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-brand-navy mb-2">{step.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                      {step.note && (
                        <p className="text-xs text-brand-green font-medium mt-3">{step.note}</p>
                      )}
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <AnimatedSection>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
              Why Choose TaxPro-Answers?
            </p>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {whyChoose.map((item, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <div className="bg-white border border-slate-200 rounded-lg p-6 h-full">
                  <div className="w-10 h-10 rounded-md bg-brand-navy/5 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-brand-navy" />
                  </div>
                  <h3 className="font-heading font-semibold text-base text-brand-navy mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-brand-navy">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <AnimatedSection>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-white mb-4">
              Take the First Step Toward Resolving Your IRS Tax Problems
            </h2>
            <p className="text-base text-slate-400 mb-3 max-w-xl mx-auto">
              Don't let unanswered IRS notices or unpaid taxes create more uncertainty.
            </p>
            <p className="text-lg font-semibold text-brand-green mb-10">Your consultation is FREE.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <a href="https://taxproanswers.setmore.com" target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="final-book-btn"
                  className="bg-brand-green hover:bg-brand-green-hover text-white font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  Schedule My Free Tax Relief Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <a href="tel:+18555892461">
                <Button
                  data-testid="final-call-btn"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 font-semibold px-8 py-7 text-base rounded-md w-full sm:w-auto"
                >
                  <Phone className="w-4 h-4 mr-2" /> Call (855) 589-2461
                </Button>
              </a>
            </div>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-slate-400">
              <a href="mailto:info@taxpro-answers.com" className="hover:text-white transition-colors">
                <Mail className="w-4 h-4 inline mr-1" /> info@taxpro-answers.com
              </a>
              <span className="hidden sm:inline">|</span>
              <a href="https://taxpro-answers.com" className="hover:text-white transition-colors">
                <Globe className="w-4 h-4 inline mr-1" /> taxpro-answers.com
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <AnimatedSection>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-green mb-4">
                Frequently Asked Questions
              </p>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-brand-navy">
                Common questions about tax relief
              </h2>
            </AnimatedSection>
            <AnimatedSection delay={0.15}>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, i) => (
                  <AccordionItem
                    key={i}
                    value={`faq-${i}`}
                    className="bg-slate-50 border border-slate-200 rounded-lg px-6 data-[state=open]:border-brand-green/30"
                  >
                    <AccordionTrigger
                      data-testid={`relief-faq-${i}`}
                      className="text-sm font-semibold text-brand-navy hover:no-underline py-4"
                    >
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-500 leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-xs text-slate-400 leading-relaxed">
            <strong>Important Pricing Information:</strong> All prices shown are starting professional service fees and are subject to case review. Actual fees depend on complexity, tax years, number of tax authorities, required documentation, and scope of representation. Tax return preparation, third-party fees, applicable IRS fees, and payments toward tax liabilities are additional unless specifically included.
          </p>
          <p className="text-xs text-slate-400 leading-relaxed mt-3">
            TaxPro-Answers is an independent tax-services business and is not affiliated with or endorsed by the Internal Revenue Service. IRS representation is performed only by professionals authorized for the particular matter. Results are not guaranteed.
          </p>
        </div>
      </section>
    </div>
  );
}
